import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useOffice } from "@/context/OfficeContext";
import api from "@/lib/api";
import {
  Bell,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  Menu,
  User as UserIcon,
  Building2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Topbar({ onMenuClick, showMenuButton = true }) {
  const { user, tenant, logout, impersonation, exitWorkspace } = useAuth();
  const { theme, toggle } = useTheme();
  const { notifications: officeNotes = [], unread: officeUnread = 0, unreadMailCount = 0, markNotificationRead, markAllRead } = useOffice();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);

  const unreadMailAlerts = officeNotes.filter((note) => !note.read && note.event === "INTERNAL_MAIL").length;
  const alertCount = notes.length + officeUnread + Math.max(0, unreadMailCount - unreadMailAlerts);
  const officeDestination = (note) => {
    if (note.event === "INTERNAL_MAIL") return "/office/mail";
    if (note.event === "CHAT_MESSAGE") return "/office/chat";
    if (note.event === "NEW_APPOINTMENT" || note.event === "MEETING_REMINDER") return "/office/calendar";
    if (note.event === "NEW_MEMO_BROADCAST") return "/office/memos";
    if (note.event?.startsWith("DOC_")) return "/office/documents";
    if (note.event?.startsWith("JOB_")) return "/office/jobs";
    return "/office";
  };

  useEffect(() => {
    let mounted = true;
    api.get("/notifications").then((r) => mounted && setNotes(r.data)).catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const initials = (user?.full_name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      {impersonation && (
        <div
          data-testid="impersonation-banner"
          className="sticky top-0 z-40 h-9 flex items-center justify-between px-4 lg:px-6 bg-brand text-obsidian text-xs font-mono"
        >
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-widest font-bold">Impersonating</span>
            <span>You are viewing <strong>{tenant?.company_name}</strong> from your super-admin home <strong>{impersonation.home_tenant?.company_name}</strong></span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-obsidian hover:bg-obsidian/10"
            onClick={async () => {
              await exitWorkspace();
              window.location.reload();
            }}
            data-testid="exit-workspace"
          >
            Exit workspace →
          </Button>
        </div>
      )}
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 h-16 border-b border-border glass-nav flex items-center gap-2 px-3 sm:gap-3 sm:px-4 lg:px-6"
    >
      {showMenuButton && <Button
        variant="ghost"
        size="icon"
        className="lg:hidden shrink-0 border-0 bg-transparent shadow-none"
        onClick={onMenuClick}
        data-testid="sidebar-toggle"
      >
        <Menu className="h-5 w-5" />
      </Button>}

      {/* Active Tenant */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <div className="h-8 w-8 shrink-0 rounded-md bg-card flex items-center justify-center overflow-hidden sm:h-9 sm:w-9">
          <img src={tenant?.logo_url || "/branding/egs-logo.jpg"} alt="logo" className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0 max-w-[42vw] sm:max-w-none" data-testid="active-tenant">
          <div className="font-display text-xs font-semibold truncate leading-tight sm:text-sm">
            {tenant?.company_name || "No tenant"}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 truncate text-[9px] font-mono text-muted-foreground sm:text-[11px]">
            <span className="truncate">TIN: {tenant?.tin_number || "—"}</span>
            {tenant?.subscription_status && (
              <Badge
                variant="outline"
                className="h-4 shrink-0 px-1.5 py-0 text-[8px] font-mono uppercase tracking-wider sm:text-[9px]"
                data-testid="tenant-subscription-badge"
              >
                {tenant.subscription_status}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1" />

      {/* Theme toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 border-0 bg-transparent shadow-none"
        onClick={toggle}
        data-testid="theme-toggle"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      {/* Notifications */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-testid="notifications-trigger" className="relative h-9 w-9 shrink-0 border-0 bg-transparent shadow-none">
            <Bell className="h-4 w-4" />
            {alertCount > 0 && (
              <span
                data-testid="notifications-badge"
                className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-brand text-obsidian text-[9px] font-mono font-bold flex items-center justify-center"
              >
                {alertCount > 99 ? "99+" : alertCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80" data-testid="notifications-panel">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Alerts</span>
            <span className="text-[10px] font-mono text-muted-foreground">{alertCount} active alerts{unreadMailCount > 0 ? ` · ${unreadMailCount} unread mail` : ""}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {officeUnread > 0 && <DropdownMenuItem onSelect={() => markAllRead?.()} className="justify-end text-[11px] text-muted-foreground" data-testid="notifications-mark-all-read">Mark Office alerts as read</DropdownMenuItem>}
          {unreadMailCount > 0 && <DropdownMenuItem onSelect={() => navigate("/office/mail")} className="flex items-center justify-between" data-testid="notifications-unread-mail"><span className="text-xs font-medium">Unread internal mail</span><Badge variant="secondary">{unreadMailCount > 99 ? "99+" : unreadMailCount}</Badge></DropdownMenuItem>}
          {notes.length === 0 && officeNotes.length === 0 && unreadMailCount === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">No alerts</div>
          )}
          {notes.map((n) => (
            <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-0.5 py-2">
              <div className="text-xs font-medium">{n.title}</div>
              <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
                {n.type}
              </div>
            </DropdownMenuItem>
          ))}
          {officeNotes.map((n) => (
            <DropdownMenuItem key={n.id} onSelect={() => { markNotificationRead?.(n.id); navigate(officeDestination(n)); }} className={`flex flex-col items-start gap-0.5 py-2 ${!n.read ? "bg-secondary/40" : ""}`} data-testid={`office-notification-${n.id}`}>
              <div className="flex w-full items-start justify-between gap-2"><span className="text-xs font-medium">{n.title}</span>{!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" />}</div>
              {n.body && <div className="line-clamp-2 text-[10px] text-muted-foreground">{n.body}</div>}
              <div className="text-[10px] font-mono text-muted-foreground uppercase">{n.event?.replaceAll("_", " ")}</div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* User dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex shrink-0 items-center gap-1 border-0 bg-transparent px-1.5 shadow-none sm:gap-2 sm:px-2"
            data-testid="user-menu-trigger"
          >
            <Avatar className="h-7 w-7">
              {user?.picture && <AvatarImage src={user.picture} />}
              <AvatarFallback className="bg-obsidian text-brand dark:bg-brand dark:text-obsidian text-[11px] font-mono font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-left">
              <div className="text-xs font-medium leading-tight">{user?.full_name}</div>
              <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
                {user?.assigned_role === "demo" ? "DEMO" : user?.role?.replace("_", " ")}
              </div>
            </div>
            <ChevronDown className="hidden h-3 w-3 opacity-60 sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-xs font-medium">{user?.email}</div>
            <div className="text-[10px] font-mono text-muted-foreground">
              {tenant?.trade_name || "Workspace"}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
            <UserIcon className="h-3.5 w-3.5 mr-2" /> Workspace Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="user-menu-logout-everywhere"
            onClick={async () => {
              await api.post("/auth/logout-everywhere");
              await logout();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out everywhere
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive-foreground focus:text-destructive-foreground"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            data-testid="user-menu-logout"
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
    </>
  );
}
